/**
 * @file useDesktopCalendar.ts
 * @brief Today's and tomorrow's meetings from the Mac's calendar, in the
 *        desktop app (DESKTOP/src/calendar.js, preload v8).
 *
 * @details
 * DES 06-10-2026: calendar sync before launch. In a browser tab, on Windows,
 * or in a desktop build older than v8, `enabled` is false and the page shows
 * nothing. The macOS prompt is only ever shown from `connect()` (a "Connect
 * your calendar" click). Reads again when the window regains focus.
 *
 * ⛔ CONNECTED IS PER VERBLIZR USER, not per Mac (the phone shipped this fix
 * after DES saw the previous user's calendar on a shared phone): macOS grants
 * the app, so without a per-user choice the next person to log in would see
 * the Mac owner's meetings. Each user clicks Connect themselves (no prompt
 * when the Mac already allows it); a user switch clears the list at once.
 *
 * Phone parity (DES 14-10-2026): only meeting-like events; "Choose calendars"
 * (hidden ids per user, so a new calendar shows by default; needs desktop
 * v11 to list calendars); after "Not now" the heading stays with Connect
 * (undoDismiss) instead of the block vanishing for good.
 *
 * Connect feedback (DES 20-10-2026: Connect did nothing in the Mac app):
 * while macOS asks, `connectPhase` is "waiting"; after 30 s with no answer
 * it is "timedOut" (the prompt can sit behind the window; a late answer still
 * counts). After Don't Allow, `openSettings` (desktop v12) opens Privacy &
 * Security > Calendars; switching it on there connects this user when the
 * window comes back, since they asked for it with that click.
 *
 * Related: ./calendarMeetings.ts, ./CalendarMeetingsPanel.tsx,
 *          FRONTEND/src/features/calendar/useCalendarMeetings.ts (phone)
 *
 * @author DES
 * @date 06-10-2026
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  desktopShell,
  type DesktopCalendarStatus,
  type DeviceCalendar,
} from "@/shared/desktop/desktopShell";
import { requestScheduleSync } from "@/features/scheduleReminders/syncSignal";
import { toCalendarMeetings, todayAndTomorrowWindow, type CalendarMeeting } from "./calendarMeetings";

const CONNECTED_KEY = (uid: string) => `vz.calendar.connected.${uid}`;
const DISMISS_KEY = (uid: string) => `vz.calendar.connectDismissed.${uid}`;
const HIDDEN_KEY = (uid: string) => `vz.calendar.hiddenCalendars.${uid}`;
/** How long the page waits for the macOS prompt before saying so. */
export const CONNECT_TIMEOUT_MS = 30_000;

export type CalendarConnectPhase = "idle" | "waiting" | "timedOut";

function readHidden(uid: string): Set<string> {
  try {
    const arr = JSON.parse(window.localStorage.getItem(HIDDEN_KEY(uid)) || "[]");
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

function writeHidden(uid: string, ids: Set<string>) {
  try {
    window.localStorage.setItem(HIDDEN_KEY(uid), JSON.stringify([...ids]));
  } catch {
    // Storage blocked: the choice lasts until the page reloads.
  }
}

function readFlag(key: string): boolean {
  try {
    return window.localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
}

function writeFlag(key: string, on: boolean) {
  try {
    if (on) window.localStorage.setItem(key, "1");
    else window.localStorage.removeItem(key);
  } catch {
    // Storage blocked: the choice lasts until the page reloads.
  }
}

export type DesktopCalendarState = {
  enabled: boolean;
  /** For THIS user: a Mac grant they never chose reads "notDetermined". */
  status: DesktopCalendarStatus | "loading";
  meetings: CalendarMeeting[];
  loaded: boolean;
  connectDismissed: boolean;
  /** "waiting" while macOS asks; "timedOut" after 30 s with no answer. */
  connectPhase: CalendarConnectPhase;
  connect: () => Promise<void>;
  /** Desktop v12+: Privacy & Security > Calendars can be opened. */
  canOpenSettings: boolean;
  openSettings: () => void;
  disconnect: () => void;
  dismissConnect: () => void;
  /** Bring the connect row back after "Not now". */
  undoDismiss: () => void;
  /** Desktop v11+: the Mac's calendars can be listed. */
  canChooseCalendars: boolean;
  calendars: DeviceCalendar[];
  loadCalendars: () => Promise<void>;
  hiddenCalendarIds: ReadonlySet<string>;
  setCalendarShown: (calendarId: string, shown: boolean) => void;
};

export function useDesktopCalendar({
  userId,
  selfEmail,
}: {
  userId?: string | number | null;
  selfEmail?: string | null;
}): DesktopCalendarState {
  const shell = desktopShell();
  const enabled = !!(shell?.calendarStatus && shell.calendarEvents && shell.requestCalendar);
  const uid = userId != null ? String(userId) : "";
  const uidRef = useRef(uid);
  uidRef.current = uid;

  const [status, setStatus] = useState<DesktopCalendarStatus | "loading">(
    enabled ? "loading" : "unsupported",
  );
  const [meetings, setMeetings] = useState<CalendarMeeting[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [connectDismissed, setConnectDismissed] = useState(false);
  const [calendars, setCalendars] = useState<DeviceCalendar[]>([]);
  const [hiddenCalendarIds, setHiddenCalendarIds] = useState<ReadonlySet<string>>(() => new Set());
  // Our own "connected" flag, so a click updates the page before storage is read again.
  const [connectedTick, setConnectedTick] = useState(0);
  const [connectPhase, setConnectPhase] = useState<CalendarConnectPhase>("idle");
  // This user opened System Settings from the denied row: access switched on
  // there connects them on return (uid it was for, or "").
  const settingsForUidRef = useRef("");

  // A different user: nothing of the last one stays on screen.
  useEffect(() => {
    setMeetings([]);
    setLoaded(false);
    setStatus(enabled ? "loading" : "unsupported");
    setConnectDismissed(uid ? readFlag(DISMISS_KEY(uid)) : false);
    setCalendars([]);
    setHiddenCalendarIds(uid ? readHidden(uid) : new Set());
    setConnectPhase("idle");
    settingsForUidRef.current = "";
  }, [enabled, uid]);

  const refresh = useCallback(async () => {
    const s = desktopShell();
    if (!enabled || !s?.calendarStatus || !s.calendarEvents) return;
    const forUid = uidRef.current;
    try {
      const os = await s.calendarStatus();
      if (uidRef.current !== forUid) return;
      if (os === "authorized" && forUid && settingsForUidRef.current === forUid) {
        settingsForUidRef.current = "";
        writeFlag(CONNECTED_KEY(forUid), true);
        requestScheduleSync();
      }
      const connected = !!forUid && readFlag(CONNECTED_KEY(forUid));
      const mine: DesktopCalendarStatus = os === "authorized" && !connected ? "notDetermined" : os;
      setStatus(mine);
      if (mine !== "authorized") {
        setMeetings([]);
        return;
      }
      const { startMs, endMs } = todayAndTomorrowWindow();
      const events = await s.calendarEvents(startMs, endMs);
      if (uidRef.current !== forUid) return;
      const hidden = readHidden(forUid);
      setHiddenCalendarIds(hidden);
      setMeetings(
        toCalendarMeetings(events ?? [], { selfEmail: selfEmail || "", hiddenCalendarIds: hidden }),
      );
      setLoaded(true);
    } catch {
      // Quiet: the calendar is an extra on this page, never a blocker.
    }
  }, [enabled, selfEmail]);

  useEffect(() => {
    void refresh();
  }, [refresh, uid, connectedTick]);

  useEffect(() => {
    if (!enabled) return;
    const onFocus = () => void refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [enabled, refresh]);

  const connect = useCallback(async () => {
    const s = desktopShell();
    if (!enabled || !uid || !s?.calendarStatus || !s.requestCalendar) return;
    const requestCalendar = s.requestCalendar;
    setConnectPhase("waiting");
    const apply = (os: DesktopCalendarStatus) => {
      if (uidRef.current !== uid) return;
      setConnectPhase("idle");
      if (os === "authorized") {
        writeFlag(CONNECTED_KEY(uid), true);
        setConnectedTick((n) => n + 1);
        // Scheduled meetings and sessions go into the calendar now (09-10-2026).
        requestScheduleSync();
      } else {
        setStatus(os);
      }
    };
    try {
      const os = await s.calendarStatus();
      if (os !== "notDetermined") {
        apply(os);
        return;
      }
      // The prompt waits for the user (the desktop gives it 5 minutes); the
      // page says so after 30 s, and a late answer still lands.
      const asked = requestCalendar().catch(() => "denied" as const);
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timedOut = new Promise<"timeout">((resolve) => {
        timer = setTimeout(() => resolve("timeout"), CONNECT_TIMEOUT_MS);
      });
      const first = await Promise.race([asked, timedOut]);
      clearTimeout(timer);
      if (first !== "timeout") {
        apply(first);
        return;
      }
      if (uidRef.current === uid) setConnectPhase("timedOut");
      const late = await asked;
      // Never answered (the desktop stops waiting after 5 minutes): the
      // timed-out line stays, Connect asks again.
      if (late !== "notDetermined") apply(late);
    } catch {
      if (uidRef.current === uid) setConnectPhase("idle");
    }
  }, [enabled, uid]);

  const canOpenSettings = enabled && typeof shell?.openCalendarSettings === "function";

  const openSettings = useCallback(() => {
    const s = desktopShell();
    if (!uid || !s?.openCalendarSettings) return;
    settingsForUidRef.current = uid;
    void s.openCalendarSettings().catch(() => false);
  }, [uid]);

  const disconnect = useCallback(() => {
    if (!uid) return;
    writeFlag(CONNECTED_KEY(uid), false);
    setMeetings([]);
    setLoaded(false);
    setStatus("notDetermined");
  }, [uid]);

  const dismissConnect = useCallback(() => {
    setConnectDismissed(true);
    if (uid) writeFlag(DISMISS_KEY(uid), true);
  }, [uid]);

  const undoDismiss = useCallback(() => {
    setConnectDismissed(false);
    if (uid) writeFlag(DISMISS_KEY(uid), false);
  }, [uid]);

  const canChooseCalendars = enabled && typeof shell?.calendarList === "function";

  const loadCalendars = useCallback(async () => {
    const s = desktopShell();
    if (!s?.calendarList) return;
    const forUid = uidRef.current;
    try {
      const list = await s.calendarList();
      if (uidRef.current === forUid) setCalendars(Array.isArray(list) ? list : []);
    } catch {
      // Quiet, as the rest of the calendar.
    }
  }, []);

  const setCalendarShown = useCallback(
    (calendarId: string, shown: boolean) => {
      if (!uid) return;
      const next = new Set(readHidden(uid));
      if (shown) next.delete(calendarId);
      else next.add(calendarId);
      writeHidden(uid, next);
      setHiddenCalendarIds(next);
      void refresh();
    },
    [uid, refresh],
  );

  return {
    enabled,
    status,
    meetings,
    loaded,
    connectDismissed,
    connectPhase,
    connect,
    canOpenSettings,
    openSettings,
    disconnect,
    dismissConnect,
    undoDismiss,
    canChooseCalendars,
    calendars,
    loadCalendars,
    hiddenCalendarIds,
    setCalendarShown,
  };
}

/**
 * The Connect row's second line (DES 20-10-2026): what macOS is doing after
 * the click, or `idle` (the usual pitch). Shared with Home's Coming up.
 */
export function connectHint(calendar: DesktopCalendarState, idle: string): string {
  if (calendar.connectPhase === "waiting") return "Waiting for macOS permission…";
  if (calendar.connectPhase === "timedOut") {
    return "macOS hasn't answered in 30 seconds. Its permission window may be behind this one: answer it there, or click to try again.";
  }
  return idle;
}
